import { OBJECT_CONTEXT_MENU_NAME } from '@ardoq/context-menu';
import {
  FieldsAndItemsSourceData,
  TimelineChartRenderingData,
  TimelineViewInterface,
  TimelineCallbacks,
  TimelineViewModelData,
  TimelineViewModelState,
  TimelineViewRenderingData,
  TimelineViewSettings,
  ViewLoadingStatus,
  ViewModelSourceData,
  XAxisLabelRowProps,
} from '../types';
import {
  DEFAULT_TIMELINE_SCALE_LIMITS,
  DEFAULT_TIME_RANGE,
  DEFAULT_WINDOW_CENTER,
  EMPTY_CHART_DIMENSIONS,
  TIMELINES_GRID_PRECISION_AUTO_OPTION_VALUE,
} from '../consts';
import { returnNull, WILDCARD } from '@ardoq/common-helpers';
import { simpleNotifier } from '@ardoq/dropdown-menu';
import { UnitOfTime } from '@ardoq/api-types';
import { getEmptyGraphModel } from '@ardoq/data-model';
import { currentDate } from '@ardoq/date-time';
import { noop } from 'lodash';
import {
  getGraphInterfaceWithModelInterfaces,
  getLabelsInterfaceWithModelInterfaces,
  getRepresentationInterfaceWithModelInterfaces,
} from '@ardoq/graph';

/** View settings relevant for recalculation of the view model data */
export const TIMELINE_VIEW_SOURCE_DATA_RELEVANT_VIEW_SETTINGS: Array<
  keyof TimelineViewSettings
> = [
  'selectedMilestoneFields',
  'selectedPhaseFields',
  'useNewGrouping',
  'depthOfRelationship',
  'incomingDegreesOfRelationship',
  'outgoingDegreesOfRelationship',
];

export const DEFAULT_TIMELINE_VIEW_SETTINGS: TimelineViewSettings = {
  isLegendActive: false,
  selectedMilestoneFields: [],
  selectedPhaseFields: [WILDCARD],
  useNewGrouping: true,
  domainRange: [null, null],
  gridPrecision: TIMELINES_GRID_PRECISION_AUTO_OPTION_VALUE,
  incomingDegreesOfRelationship: 0,
  outgoingDegreesOfRelationship: 0,
  depthOfRelationship: 1,
  persistedCollapsedState: { collapsedGroupIds: [], collapsedFieldDataIds: [] },
};

const EMPTY_X_AXIS_LABEL_ROW_PROPS: XAxisLabelRowProps = {
  rowHeight: 0,
  dimensions: EMPTY_CHART_DIMENSIONS.dimensions,
  noVisibleTicksRowLabel: null,
  tickLabelsToDisplay: [],
};

export const EMPTY_TIMELINE_CHART_AND_ITEMS_RENDERING_DATA: TimelineChartRenderingData =
  {
    timelineChartRangeConstraints: {
      availableExpandedDomain: DEFAULT_TIME_RANGE,
      scalingLimitsForAdjustedGrid: DEFAULT_TIMELINE_SCALE_LIMITS,
    },
    timelineChartProperties: {
      chartWindow: { windowCenter: DEFAULT_WINDOW_CENTER, windowScale: 1 },
      clientRect: { width: 0, height: 0 },
      visibleDomain: DEFAULT_TIME_RANGE,
      adjustedGridPrecision: UnitOfTime.QUARTER,
      dimensions: EMPTY_CHART_DIMENSIONS.dimensions,
      unscaleX: () => currentDate(),
      todayPosition: 0,
      labelsColumnMinWidth: 0,
      userDefinedLabelsColumnWidth: null,
      phasesScaler: () => 0,
      xAxisProps: {
        topLabelRowProps: null,
        bottomLabelRowProps: EMPTY_X_AXIS_LABEL_ROW_PROPS,
        xAxisContainerHeight: 0,
        visibleDomainRowLabel: '',
      },
      crosshairXProps: null,
    },
  };

export const DEFAULT_TIMELINE_CALLBACKS: TimelineCallbacks = {
  openDateRangeViewSetting: simpleNotifier(),
  openMilestonesViewSetting: simpleNotifier(),
  onViewSettingsUpdate: noop,
  isPresentationMode: () => false,
  getNeverShowAgain: async () => undefined,
  setNeverShowAgain: noop,
};

const DEFAULT_TIMELINE_VIEW_MODEL_DATA: TimelineViewModelData = {
  viewModelData: [],
  viewModelDataDomain: DEFAULT_TIME_RANGE,
  filteredViewGraphHierarchy: new Map(),
  collapsibleGroups: {
    collapsible: new Set(),
    rootCollapsible: [],
  },
  collapsibleFieldDataIds: {
    collapsible: new Set(),
    rootCollapsible: [],
  },
  timelineViewFieldsRegistry: {
    timeSpanFields: [],
    milestoneFields: [],
    orderedSelectedPhaseFields: [],
    orderedSelectedMilestoneFields: [],
  },
  legendComponentTypes: [],
  hasGrouping: false,
};

export const DEFAULT_TIMELINE_CHART_AND_ITEMS_RENDERING_DATA: TimelineViewRenderingData =
  {
    timelineChartRenderingData: EMPTY_TIMELINE_CHART_AND_ITEMS_RENDERING_DATA,
    collapsedGroupIds: new Set(),
    collapsedFieldDataIds: new Set(),
    allItemsWithRenderingData: {
      allItemLabelsWithRenderingData: [],
      allItemPhasesWithRenderingData: [],
    },
    itemLabelsWithRenderingDataWithoutCollapsedHierarchy: [],
    itemPhasesWithRenderingDataWithoutCollapsedHierarchy: [],
    itemLabelsWithCollapsedFieldDataState: [],
    itemPhasesWithCollapsedFieldDataState: [],
    labelsInVirtualWindow: [],
    phasesInVirtualWindow: [],
    renderedItemLabels: [],
    renderedItemPhases: [],
    allItemTopsWithSentinel: [],
    itemTopsInVirtualWindow: [],
    backgroundScrollHeight: 0,
    virtualScrollState: {
      scrollTop: 0,
      firstItemIndex: 0,
      lastItemIndex: 0,
    },
    transitLinksById: new Map(),
    renderedPhasesByGraphNodeId: {},
    renderedLabelsByGraphNodeId: new Map(),
  };

const DEFAULT_VIEW_INTERFACE: TimelineViewInterface = {
  ...getGraphInterfaceWithModelInterfaces(),
  ...getRepresentationInterfaceWithModelInterfaces(),
  ...getLabelsInterfaceWithModelInterfaces(),
  getComponentWorkspaceId: returnNull,
  getComponentFieldValue: returnNull,
  getAllComponentIds: () => [],
  getAllComponents: () => [],
  getComponentTypesForWorkspace: () => [],
  getWorkspaceNameById: returnNull,
  getFieldsOfWorkspace: () => [],
  getAllGroupingRules: () => [],

  features: {
    hasRootGroupsInContextOnlyFeature: false,
    isModernizedStyle: false,
  },
  timelineCallbacks: DEFAULT_TIMELINE_CALLBACKS,
};

const DEFAULT_FIELDS_AND_ITEMS_SOURCE_DATA: FieldsAndItemsSourceData = {
  viewGraphHierarchy: new Map(),
  orderedSelectedDateRangeFields: [],
  orderedSelectedMilestoneFields: [],
  startSetNodes: [],
  relevantDateRangeFieldsWithWorkspaceNames: [],
  relevantMilestoneFieldsWithWorkspaceNames: [],
};

export const DEFAULT_TIMELINE_VIEW_MODEL_SOURCE_DATA: ViewModelSourceData = {
  graph: getEmptyGraphModel(),
  errors: [],
  hasClones: false,
  sourceDataViewSettings: DEFAULT_TIMELINE_VIEW_SETTINGS,
  viewInterface: DEFAULT_VIEW_INTERFACE,
  viewRelevantComponentsScopeData: new Map(),
  workspaceAndFieldsRegistry: new Map(),
  fieldsAndItemsSourceData: DEFAULT_FIELDS_AND_ITEMS_SOURCE_DATA,
  contextComponentId: '',
  viewInstanceId: '',
  hasGroupingStyles: false,
  contextMenuName: OBJECT_CONTEXT_MENU_NAME,
  contextMenuDataAttributeName: '',
  isViewEmpty: false,
  users: [],
  i18nLocale: {},
  errorInfoBoxLabels: {},
};

export const DEFAULT_TIMELINE_VIEW_MODEL_STATE: TimelineViewModelState = {
  viewModelSourceData: DEFAULT_TIMELINE_VIEW_MODEL_SOURCE_DATA,
  viewStatus: ViewLoadingStatus.LOADING,
  viewSettings: DEFAULT_TIMELINE_VIEW_SETTINGS,
  timelineViewModelData: DEFAULT_TIMELINE_VIEW_MODEL_DATA,
  timelineViewRenderingData: DEFAULT_TIMELINE_CHART_AND_ITEMS_RENDERING_DATA,
  hoveredItemId: null,
  focusedItemId: null,
  getTimelineContentContainer: returnNull,
  isRenderingForExport: false,
  collapsedStateOrigin: 'data',
};

export const DOMAIN_RANGE_LABEL_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

export const TIMELINE_CHART_DIMENSIONS_MARGIN_TOP = 20;
